<template>
  <InvestorOnboardingStep>
    <View direction="column" gap="xl" align="start">
      <View direction="column" gap="xs">
        <View direction="row" gap="sm" align="center">
          <Text variant="h2">Connect your calendar</Text>
          <Badge label="Optional" variant="grey" size="small" />
        </View>
        <Text variant="body-medium" color="secondary">
          One connection, and every showing lands on your calendar without you lifting a finger.
        </Text>
      </View>

      <View direction="row" gap="md" align="center" class="provider">
        <IntegrationLogo :tile="{ logoIcon: 'g-calendar' }" alt="Google Calendar" />
        <View direction="column" gap="xs" class="provider-copy">
          <Text variant="label-medium">Google Calendar</Text>
          <Text variant="body-small" color="secondary">Showings sync to your calendar in real time</Text>
        </View>
        <Badge v-if="isConnected" label="Connected" variant="green" size="small" icon="check-circle" />
        <Badge v-else label="Not connected" variant="grey" size="small" />
      </View>

      <View direction="column" gap="md">
        <View v-for="benefit in benefits" :key="benefit.title" direction="row" gap="sm" align="center">
          <IconTile :icon="benefit.icon" size="small" color="blue" :coloredIcon="true" />
          <View direction="column">
            <Text variant="label-medium">{{ benefit.title }}</Text>
            <Text variant="body-small" color="secondary">{{ benefit.description }}</Text>
          </View>
        </View>
      </View>

      <View direction="column" gap="sm">
        <Button
          v-if="!isConnected"
          variant="primary"
          size="medium"
          :loading="isConnecting"
          :disabled="isConnecting || isSaving"
          @click="connectCalendar"
        >
          Connect Google Calendar
        </Button>
        <Button
          v-else
          variant="secondary"
          size="medium"
          :loading="isConnecting"
          :disabled="isConnecting || isSaving"
          @click="disconnect"
        >
          Disconnect Google Calendar
        </Button>
        <Text variant="body-small" color="tertiary">
          You can connect or change this later in Settings, under Integrations.
        </Text>
      </View>

      <View direction="row" gap="xs" class="nav-buttons">
        <Button variant="ghost" @click="goBack" :disabled="typing || isConnecting || isSaving">Back</Button>
        <Button
          v-if="isConnected"
          @click="finishStep"
          :disabled="typing || isConnecting || isSaving"
          :loading="isSaving"
        >
          Continue
        </Button>
        <Button v-else variant="ghost" @click="skip" :disabled="typing || isConnecting || isSaving" :loading="isSaving">
          Skip for now
        </Button>
      </View>
    </View>
  </InvestorOnboardingStep>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { CalendarProvider, googleCalendarService } from '@tallo/auth';
import { Badge, Button, IconTile, Text, View, WhatToExpectItem, useAlertsStore } from '@tallo/design-system';
import {
  InvestorOnboardingStep as OnboardingStep,
  useInvestorStore,
  useOnboardingStep,
  useOnboardingStore,
} from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';

import IntegrationLogo from '../../integrations/integration-logo.vue';
import InvestorOnboardingStep from '../components/investor-onboarding-step.vue';

const router = useRouter();
const store = useOnboardingStore();
const investorStore = useInvestorStore();
const alerts = useAlertsStore();

// The same promises the Integrations page makes, in one line each
const benefits: WhatToExpectItem[] = [
  { icon: 'calendar', title: 'Showings added for you', description: 'Every confirmed tour lands on your calendar.' },
  { icon: 'refresh', title: 'Always up to date', description: 'Reschedules and cancellations sync automatically.' },
  {
    icon: 'sparkle',
    title: 'Smarter time suggestions',
    description: 'Tallo steers renters away from slots you are busy.',
  },
];

const isConnected = computed(() => {
  const googleCalendar = investorStore.investor?.user?.calendars?.find(
    (calendar) => calendar.provider === CalendarProvider.GOOGLE,
  );
  return !!googleCalendar;
});
const isConnecting = ref(false);
const oauthWindow = ref<Window | null>(null);
const windowCheckInterval = ref<number | null>(null);

const { typing, goBack, isSaving, save, onContinue } = useOnboardingStep(() => !isConnecting.value);

onMounted(() => {
  window.addEventListener('message', handleMessage);
});
onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  if (windowCheckInterval.value) {
    clearInterval(windowCheckInterval.value);
  }
});

async function handleMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin) return;
  if (event.data?.type !== 'GOOGLE_CALENDAR_CONNECTED') return;
  if (oauthWindow.value && event.source !== oauthWindow.value) return;

  resetConnectionState();

  if (event.data.success) {
    await investorStore.load();
    alerts.success('Google Calendar connected successfully');
    finishStep();
  } else {
    alerts.error('Failed to connect Google Calendar');
  }

  // Remove listener after handling once
  window.removeEventListener('message', handleMessage);
}

function resetConnectionState() {
  isConnecting.value = false;
  oauthWindow.value = null;
  if (windowCheckInterval.value) {
    clearInterval(windowCheckInterval.value);
    windowCheckInterval.value = null;
  }
}

// Connected or skipped, the step counts as done; the calendar link itself lives on the user
async function finishStep() {
  await save(async () => {
    await store.saveStep(OnboardingStep.ConnectCalendar, {});
    router.push({ name: AppRouteNames.INVESTOR_ONBOARDING_COMMUNICATION });
  }, "We couldn't save your progress. Please try again.");
}

function skip() {
  finishStep();
}

// Enter continues once the calendar is linked, or skips while it is not
onContinue(() => (isConnected.value ? finishStep() : skip()));

async function disconnect() {
  if (isConnecting.value) return; // Prevent double-clicking

  isConnecting.value = true;
  try {
    await googleCalendarService.disconnect(CalendarProvider.GOOGLE);
    await investorStore.load();
    alerts.success('Google Calendar disconnected');
  } catch {
    alerts.error('Failed to disconnect Google Calendar');
  } finally {
    isConnecting.value = false;
  }
}

async function connectCalendar() {
  if (isConnecting.value) return; // Prevent double-clicking

  isConnecting.value = true;
  try {
    const authUrl = await googleCalendarService.connect();
    const url = new URL(authUrl);
    url.searchParams.append('isPrimary', 'true');

    // Open OAuth flow directly; callback page will postMessage back to us
    // Note: Do NOT use 'noopener'/'noreferrer' here because we rely on window.opener.postMessage from the callback window.
    oauthWindow.value = window.open(url.toString(), '_blank');

    if (!oauthWindow.value) {
      alerts.error('The sign-in window was blocked. Please allow popups and try again.');
      resetConnectionState();
      return;
    }

    // Unlock the step if the user closes the OAuth window without completing the flow;
    // otherwise isConnecting stays true and Back/Skip remain disabled forever
    windowCheckInterval.value = window.setInterval(() => {
      if (oauthWindow.value?.closed) {
        resetConnectionState();
      }
    }, 500);
  } catch (error) {
    console.error('Failed to initiate Google Calendar connection:', error);
    alerts.error('Failed to initiate Google Calendar connection');
    resetConnectionState();
  }
}
</script>

<style scoped>
.provider {
  width: 100%;
  padding: var(--t-spacing-md);
  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
}
.provider-copy {
  flex: 1;
  min-width: 0;
}
.nav-buttons {
  margin-left: auto;
}
</style>
